import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { JobCard, JobFilters, JobSearch, JobPagination } from './components';
import { LoadingSpinner } from '../../components/layout';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { useJobs } from '../../hooks/unified/useJobsQueries';
import { useViewport } from '../../hooks/useViewport';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';
import type { Job, JobFilters as JobFiltersType, JobSearchParams } from '../../types/job';
import type { Job as UnifiedJob } from '../../services/unified/types';

export function JobsPage() {
  useSEO(SEO_CONFIGS.jobs);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const { isMobile, prefersReducedMotion } = useViewport();
  
  const { data: apiJobs, isLoading, error: apiError } = useJobs();

  // Parse filters from URL
  const filters = useMemo((): JobFiltersType => {
    return {
      search: searchParams.get('search') || '',
      location: searchParams.get('location') || '',
      company: searchParams.get('company') || '',
      postedWithin: searchParams.get('posted_within') as JobFiltersType['postedWithin'] || 'all',
      jobType: searchParams.getAll('job_type').length > 0 ? searchParams.getAll('job_type') : undefined,
      experience: searchParams.getAll('experience').length > 0 ? searchParams.getAll('experience') : undefined,
      salaryRange: searchParams.get('salary_min') && searchParams.get('salary_max') 
        ? [parseInt(searchParams.get('salary_min')!), parseInt(searchParams.get('salary_max')!)] 
        : undefined
    };
  }, [searchParams]);

  const searchConfig = useMemo((): JobSearchParams => ({
    page: parseInt(searchParams.get('page') || '1'),
    limit: 12,
    sortBy: searchParams.get('sort_by') as JobSearchParams['sortBy'] || 'relevance',
    sortOrder: searchParams.get('sort_order') as JobSearchParams['sortOrder'] || 'desc',
    filters
  }), [searchParams, filters]);

  const updateFilters = (newFilters: Partial<JobFiltersType>) => {
    const newSearchParams = new URLSearchParams();
    const updatedFilters = { ...filters, ...newFilters };
    
    if (updatedFilters.search) newSearchParams.set('search', updatedFilters.search);
    if (updatedFilters.location) newSearchParams.set('location', updatedFilters.location);
    if (updatedFilters.company) newSearchParams.set('company', updatedFilters.company);
    if (updatedFilters.postedWithin && updatedFilters.postedWithin !== 'all') {
      newSearchParams.set('posted_within', updatedFilters.postedWithin);
    }
    updatedFilters.jobType?.forEach(type => newSearchParams.append('job_type', type));
    updatedFilters.experience?.forEach(exp => newSearchParams.append('experience', exp));
    if (updatedFilters.salaryRange) {
      newSearchParams.set('salary_min', updatedFilters.salaryRange[0].toString());
      newSearchParams.set('salary_max', updatedFilters.salaryRange[1].toString());
    }
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
    
    // Auto-close filter sheet on mobile after applying
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const updateSorting = (sortBy: JobSearchParams['sortBy'], sortOrder: JobSearchParams['sortOrder']) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('sort_by', sortBy || 'relevance');
    newSearchParams.set('sort_order', sortOrder || 'desc');
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const updatePage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page.toString());
    setSearchParams(newSearchParams);
  };

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    if (!apiJobs) return [];
    
    let filtered = apiJobs
      .filter((job: UnifiedJob) => !job.status || job.status === 'active')
      .map((job: UnifiedJob): Job & UnifiedJob => ({
        id: job.id,
        title: job.title,
        summary: job.summary,
        description: job.description,
        location: job.location,
        company: job.company,
        salary: job.salary_min && job.salary_max 
          ? `₹${job.salary_min.toLocaleString()} - ₹${job.salary_max.toLocaleString()}`
          : undefined,
        requirements: job.requirements,
        benefits: job.benefits,
        postedDate: job.created_at || new Date().toISOString(),
        type: job.job_type as Job['type'],
        experience: job.experience_level as Job['experience'],
        companyLogo: job.company_logo_url,
        salary_min: job.salary_min,
        salary_max: job.salary_max,
        job_type: job.job_type,
        experience_level: job.experience_level,
        company_logo_url: job.company_logo_url,
        job_image_url: job.job_image_url,
        status: job.status,
        created_at: job.created_at,
        updated_at: job.updated_at,
        published_at: job.published_at,
      }));

    // Apply filters
    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(term) ||
        job.description?.toLowerCase().includes(term) ||
        job.company?.toLowerCase().includes(term)
      );
    }
    if (filters.location) {
      filtered = filtered.filter(job => job.location?.toLowerCase().includes(filters.location!.toLowerCase()));
    }
    if (filters.company) {
      filtered = filtered.filter(job => job.company?.toLowerCase().includes(filters.company!.toLowerCase()));
    }
    if (filters.jobType?.length) {
      filtered = filtered.filter(job => filters.jobType!.includes(job.job_type || job.type || ''));
    }
    if (filters.experience?.length) {
      filtered = filtered.filter(job => filters.experience!.includes(job.experience_level || job.experience || ''));
    }
    if (filters.salaryRange) {
      const [min, max] = filters.salaryRange;
      filtered = filtered.filter(job => 
        job.salary_max !== undefined && job.salary_min !== undefined &&
        job.salary_max >= min && job.salary_min <= max
      );
    }
    if (filters.postedWithin && filters.postedWithin !== 'all') {
      const cutoff = new Date();
      if (filters.postedWithin === 'day') cutoff.setDate(cutoff.getDate() - 1);
      else if (filters.postedWithin === 'week') cutoff.setDate(cutoff.getDate() - 7);
      else if (filters.postedWithin === 'month') cutoff.setMonth(cutoff.getMonth() - 1);
      filtered = filtered.filter(job => new Date(job.postedDate || job.created_at || '') >= cutoff);
    }

    // Sort
    filtered.sort((a, b) => {
      const order = searchConfig.sortOrder === 'asc' ? 1 : -1;
      switch (searchConfig.sortBy) {
        case 'date':
          return order * (new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
        case 'title':
          return order * a.title.localeCompare(b.title);
        case 'salary':
          return order * ((b.salary_min || 0) - (a.salary_min || 0));
        default:
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
      }
    });

    return filtered;
  }, [apiJobs, filters, searchConfig.sortBy, searchConfig.sortOrder]);

  // Pagination - fewer items per page on mobile
  const itemsPerPage = isMobile ? 8 : 12;
  const totalJobs = filteredJobs.length;
  const totalPages = Math.ceil(totalJobs / itemsPerPage);
  const jobs = filteredJobs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(searchConfig.page || 1);
  }, [searchConfig.page]);

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => 
    Array.isArray(v) ? v.length > 0 : v && v !== 'all'
  );

  const activeFilterCount = [
    filters.search,
    filters.location,
    filters.company,
    filters.postedWithin !== 'all' ? filters.postedWithin : null,
    ...(filters.jobType || []),
    ...(filters.experience || []),
    filters.salaryRange ? 'salary' : null
  ].filter(Boolean).length;

  // Load more handler for mobile
  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      updatePage(currentPage + 1);
    }
  };

  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-300';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
              Find Your Perfect Job
            </h1>
            <p className="text-sm sm:text-lg text-slate-300 mb-6 sm:mb-8">
              {totalJobs} opportunities waiting for you
            </p>
            
            {/* Search Bar */}
            <JobSearch 
              initialSearch={filters.search || ''}
              onSearch={(search: string) => updateFilters({ search })}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowFilters(true)}
            className={`touch-target w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl border border-gray-200 shadow-sm ${transitionClasses}`}
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="font-medium text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Mobile Filter Bottom Sheet */}
        {isMobile && (
          <BottomSheet
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            title="Filters"
            snapPoints={[0.6, 0.9]}
          >
            <div className="p-4">
              <JobFilters
                filters={filters}
                onFiltersChange={updateFilters}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
              />
              {/* Apply button for mobile */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowFilters(false)}
                  className="touch-target w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700"
                >
                  Show {totalJobs} Results
                </button>
              </div>
            </div>
          </BottomSheet>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block lg:w-72 flex-shrink-0">
            <div className="sticky top-20">
              <JobFilters
                filters={filters}
                onFiltersChange={updateFilters}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {totalJobs} {totalJobs === 1 ? 'Job' : 'Jobs'} Found
                </h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 mt-1"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              {/* Sort */}
              <select
                value={`${searchConfig.sortBy}-${searchConfig.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-') as [JobSearchParams['sortBy'], JobSearchParams['sortOrder']];
                  updateSorting(sortBy, sortOrder);
                }}
                className="touch-target px-3 sm:px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="relevance-desc">Most Relevant</option>
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="salary-desc">Highest Salary</option>
                <option value="salary-asc">Lowest Salary</option>
              </select>
            </div>

            {/* Error State */}
            {apiError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 sm:mb-6">
                <p className="text-red-800 text-sm sm:text-base">Failed to load jobs. Please try again.</p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && jobs.length === 0 && (
              <div className="text-center py-12 sm:py-16 bg-white rounded-xl sm:rounded-2xl border border-gray-100">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
                <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">Try adjusting your filters or search terms</p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="touch-target px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Jobs Grid - Single column on mobile */}
            {jobs.length > 0 && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 sm:mb-8">
                  {jobs.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>

                {/* Mobile: Load More Button */}
                {isMobile && currentPage < totalPages && (
                  <div className="text-center mb-6">
                    <button
                      onClick={handleLoadMore}
                      className="touch-target w-full sm:w-auto px-8 py-3 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50"
                    >
                      Load More Jobs
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Showing {jobs.length} of {totalJobs} jobs
                    </p>
                  </div>
                )}

                {/* Desktop: Pagination */}
                {!isMobile && totalPages > 1 && (
                  <JobPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={updatePage}
                  />
                )}
              </>
            )}

            {/* CTA */}
            {jobs.length > 0 && (
              <div className="mt-8 sm:mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center text-white">
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Can't find what you're looking for?</h3>
                <p className="text-primary-100 text-sm sm:text-base mb-4 sm:mb-6">Create a profile and let employers find you</p>
                <Link
                  to="/login"
                  className="touch-target inline-flex items-center px-6 py-3 bg-white text-primary-700 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
